import React, { useMemo, useState } from "react";
import {
    Box, Grid, Paper, Typography, Chip, Tooltip, IconButton,
    CircularProgress, TablePagination, TextField, MenuItem,
} from "@mui/material";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import RefreshIcon from "@mui/icons-material/Refresh";

import { SECTION_COLORS, ROWS_PER_PAGE } from "../../constants";
import EmptyState from "../shared/EmptyState";
import ErrorState from "../shared/ErrorState";
import StatusChip from "../shared/StatusChip";
import ListToolbar from "../shared/ListToolbar";
import { canRecomputeDynamicSegment } from "./segmentConditionsUtils";

const fmtDate = (v) => v ? new Date(v).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "–";

const SegmentsTab = ({
    segmentsApi,
    pages,
    handlePageChange,
    onView,
    onEdit,
    onDelete,
    onViewMembers,
    onRecomputeSegment,
    tabIndex = 6,
    usesUnomi = false,
}) => {
    const [search, setSearch] = useState("");
    const [activeFilter, setActiveFilter] = useState("all");
    const [kindFilter, setKindFilter] = useState("all"); // all|dynamic|static
    const [providerFilter, setProviderFilter] = useState("all"); // all|INTERNAL|UNOMI
    const [sortBy, setSortBy] = useState("name_asc");

    const sourceData = Array.isArray(segmentsApi.data) ? segmentsApi.data : [];

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const rows = sourceData.filter((s) => {
            if (activeFilter === "active" && !s.active) return false;
            if (activeFilter === "inactive" && s.active) return false;
            if (kindFilter === "dynamic" && !s.is_dynamic) return false;
            if (kindFilter === "static" && s.is_dynamic) return false;
            if (providerFilter !== "all" && String(s.provider || "INTERNAL") !== providerFilter) return false;
            if (!q) return true;
            return String(s.name || "").toLowerCase().includes(q)
                || String(s.description || "").toLowerCase().includes(q)
                || String(s.id || "").toLowerCase().includes(q)
                || String(s.unomi_segment_id || "").toLowerCase().includes(q);
        });
        rows.sort((a, b) => {
            if (sortBy === "members_desc") return Number(b.member_count || 0) - Number(a.member_count || 0);
            if (sortBy === "updated_desc") return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
            return String(a.name || "").localeCompare(String(b.name || ""), "fr");
        });
        return rows;
    }, [sourceData, search, activeFilter, kindFilter, providerFilter, sortBy]);

    if (segmentsApi.error) return <ErrorState error={segmentsApi.error} onRetry={() => segmentsApi.refetch()} />;
    if (segmentsApi.loading) return <Box py={3} display="flex" justifyContent="center"><CircularProgress size={28} /></Box>;
    if (sourceData.length === 0) {
        return <EmptyState message="Aucun segment configuré" color={SECTION_COLORS.segments.main} />;
    }

    const pageRaw = pages[tabIndex] ?? 0;
    const maxPage = Math.max(0, Math.ceil(filtered.length / ROWS_PER_PAGE) - 1);
    const page = Math.min(pageRaw, maxPage);
    const paginated = filtered.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

    return (
        <Box>
            <ListToolbar
                search={search}
                onSearch={setSearch}
                activeFilter={activeFilter}
                onActiveFilter={setActiveFilter}
                searchPlaceholder="Rechercher un segment (nom, id, unomi_segment_id)…"
                sortValue={sortBy}
                onSortChange={setSortBy}
                sortOptions={[
                    { value: "name_asc", label: "Nom (A-Z)" },
                    { value: "members_desc", label: "Membres (décroissant)" },
                    { value: "updated_desc", label: "Mise à jour (récent)" },
                ]}
            >
                <TextField
                    select
                    size="small"
                    label="Type"
                    value={kindFilter}
                    onChange={(e) => setKindFilter(e.target.value)}
                    sx={{ minWidth: 150, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                >
                    <MenuItem value="all">Tous</MenuItem>
                    <MenuItem value="dynamic">Dynamiques</MenuItem>
                    <MenuItem value="static">Statiques</MenuItem>
                </TextField>
                <TextField
                    select
                    size="small"
                    label="Provider"
                    value={providerFilter}
                    onChange={(e) => setProviderFilter(e.target.value)}
                    sx={{ minWidth: 160, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                >
                    <MenuItem value="all">Tous</MenuItem>
                    <MenuItem value="INTERNAL">INTERNAL</MenuItem>
                    <MenuItem value="UNOMI">UNOMI</MenuItem>
                </TextField>
            </ListToolbar>
            <Grid container spacing={2}>
                {paginated.map((segment) => {
                    const hasConditions = segment.is_dynamic && segment.conditions && Object.keys(segment.conditions).length > 0;
                    return (
                        <Grid item xs={12} sm={6} md={4} key={segment.id}>
                            <Paper elevation={0} sx={{
                                p: 2.5, borderRadius: 3, position: "relative", overflow: "hidden",
                                border: `1px solid ${SECTION_COLORS.segments.main}15`,
                                background: `linear-gradient(135deg, ${SECTION_COLORS.segments.main}06 0%, transparent 100%)`,
                                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.10), 0 2px 8px rgba(15, 23, 42, 0.06)",
                                transition: "all 0.25s ease",
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 14px 30px rgba(15, 23, 42, 0.14), 0 6px 14px rgba(15, 23, 42, 0.10)",
                                    borderColor: `${SECTION_COLORS.segments.main}30`,
                                },
                            }}>
                                <Box position="absolute" top={10} right={10} display="flex" flexDirection="column" gap={0.5} alignItems="flex-end">
                                    <Tooltip title={segment.is_dynamic ? "Segment dynamique" : "Segment statique"} placement="left">
                                        <Chip
                                            label={segment.is_dynamic ? "Dynamique" : "Statique"}
                                            size="small"
                                            sx={{
                                                fontWeight: 700, fontSize: 10,
                                                bgcolor: segment.is_dynamic ? "#f0f9ff" : "#f8fafc",
                                                color: segment.is_dynamic ? "#0369a1" : "#475569",
                                            }}
                                        />
                                    </Tooltip>
                                    {(segment.provider === "UNOMI" || usesUnomi) && (
                                        <Chip label="Unomi" size="small"
                                            sx={{ fontWeight: 700, fontSize: 10, bgcolor: "#fdf4ff", color: "#a21caf" }} />
                                    )}
                                </Box>

                                <Box display="flex" alignItems="center" gap={1} mb={0.5} mr={12}>
                                    <GroupWorkIcon sx={{ color: SECTION_COLORS.segments.main, fontSize: 20, flexShrink: 0 }} />
                                    <Typography fontWeight={700} fontSize={14} fontFamily="Montserrat, sans-serif" color="#1a1a2e" noWrap>
                                        {segment.name}
                                    </Typography>
                                </Box>

                                <Typography fontSize={11} color="text.secondary" noWrap mb={1.5} sx={{ pl: 3.5 }}>
                                    {segment.description || "Aucune description"}
                                </Typography>

                                <Box display="flex" gap={1} mb={1.5} flexWrap="wrap">
                                    {hasConditions && (
                                        <Chip
                                            label="Conditions"
                                            size="small"
                                            sx={{ fontSize: 10, fontWeight: 600, bgcolor: "#fffbeb", color: "#92400e" }}
                                        />
                                    )}
                                    {typeof segment.member_count === "number" && (
                                        <Chip
                                            label={`${segment.member_count} membre(s)`}
                                            size="small"
                                            sx={{ fontSize: 10, fontWeight: 600, bgcolor: "#ecfdf5", color: "#047857" }}
                                        />
                                    )}
                                    {segment.is_dynamic && (
                                        <Chip
                                            label={segment.needs_recompute ? "Recalcul requis" : `Calculé: ${fmtDate(segment.last_computed_at)}`}
                                            size="small"
                                            sx={{
                                                fontSize: 10, fontWeight: 600,
                                                bgcolor: segment.needs_recompute ? "#fff7ed" : "#fafbfd",
                                                color: segment.needs_recompute ? "#c2410c" : "#64748b",
                                            }}
                                        />
                                    )}
                                    {segment.conditions_format === "unomi_only" && (
                                        <Chip label="CDP only" size="small"
                                            sx={{ fontSize: 10, fontWeight: 600, bgcolor: "#fff7ed", color: "#c2410c" }} />
                                    )}
                                    {segment.can_delete === false && (
                                        <Chip label="Référencé" size="small"
                                            sx={{ fontSize: 10, fontWeight: 600, bgcolor: "#fef2f2", color: "#b91c1c" }} />
                                    )}
                                </Box>

                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <StatusChip active={segment.active} />
                                    <Box display="flex" gap={0.5}>
                                        {onViewMembers && (
                                            <Tooltip title={segment.is_dynamic ? "Voir les membres" : "Gérer les membres"}>
                                                <IconButton size="small" onClick={() => onViewMembers(segment)}
                                                    sx={{
                                                        color: SECTION_COLORS.segments.main,
                                                        bgcolor: `${SECTION_COLORS.segments.main}12`,
                                                        "&:hover": { bgcolor: `${SECTION_COLORS.segments.main}22` },
                                                        width: 30, height: 30,
                                                    }}>
                                                    <PeopleAltIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {segment.is_dynamic && onRecomputeSegment && canRecomputeDynamicSegment(segment) && (
                                            <Tooltip title="Recalcul rapide (POST recompute)">
                                                <IconButton size="small" onClick={() => onRecomputeSegment(segment)}
                                                    sx={{
                                                        color: "#0369a1",
                                                        bgcolor: "#f0f9ff",
                                                        "&:hover": { bgcolor: "#e0f2fe" },
                                                        width: 30, height: 30,
                                                    }}>
                                                    <RefreshIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Voir le détail">
                                            <IconButton size="small" onClick={() => onView(segment)}
                                                sx={{
                                                    color: SECTION_COLORS.segments.main,
                                                    bgcolor: `${SECTION_COLORS.segments.main}10`,
                                                    "&:hover": { bgcolor: `${SECTION_COLORS.segments.main}20` }, width: 30, height: 30,
                                                }}>
                                                <VisibilityIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Modifier">
                                            <IconButton size="small" onClick={() => onEdit(segment)}
                                                sx={{ color: "#6366f1", bgcolor: "#eef2ff", "&:hover": { bgcolor: "#e0e7ff" }, width: 30, height: 30 }}>
                                                <EditIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Supprimer">
                                            <IconButton size="small" onClick={() => onDelete(segment)}
                                                sx={{ color: "#DC2626", bgcolor: "#fef2f2", "&:hover": { bgcolor: "#fee2e2" }, width: 30, height: 30 }}>
                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>

            {filtered.length > ROWS_PER_PAGE && (
                <TablePagination component="div" count={filtered.length} page={page}
                    onPageChange={handlePageChange(tabIndex)} rowsPerPage={ROWS_PER_PAGE} rowsPerPageOptions={[ROWS_PER_PAGE]}
                    sx={{ mt: 1, borderTop: "1px solid #f0f0f0" }} />
            )}
        </Box>
    );
};

export default SegmentsTab;
